import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import classnames from 'classnames';
import { Icon, Loader, Modal, Text } from '@Sage/hoi-poi-ui';
import BoolField from 'containers/components/Fields/Bool';
import AbsoluteError from 'components/AbsoluteError';
import Context from 'managers/Context';
import { getLiteral, getLiteralWithParameters } from 'utils/getLiteral';
import { errorToast } from 'utils/toast';
import { PATH_IMAGE } from 'constants/Environment';

import MailChimpNotExported from './MailChimpNotExported';
import ValueList from './fields/valueList';
import TextField from './fields/textField';

import './styles.scss';

const MailChimpDialog = ({ filters, onCloseCallback, show }) => {
    const canCreate = useSelector(
        (state) =>
            state.config.permission.mailChimp && state.config.permission.createMailchimpLists,
    );

    const [open, setOpen] = useState(!!show);
    const [loading, setLoading] = useState(true);
    const [companies, setCompanies] = useState(0);
    const [contacts, setContacts] = useState(0);
    const [mailChimpLists, setMailChimpLists] = useState([]);
    const [selectAccounts, setSelectAccounts] = useState(false);
    const [selectContacts, setSelectContacts] = useState(false);
    const [selectedList, setSelectedList] = useState(null);
    const [newList, setNewList] = useState('');
    const [newListExist, setNewListExist] = useState(false);
    const [isList, setIsList] = useState(true);
    const [currentSection, setCurrentSection] = useState(false);
    const [isExporting, setIsExporting] = useState(false);
    const [exportFinished, setExportFinished] = useState(false);
    const [companiesExportedCount, setCompaniesExportedCount] = useState(0);
    const [contactsExportedCount, setContactsExportedCount] = useState(0);
    const [companiesNotExportedCount, setCompaniesNotExportedCount] = useState(0);
    const [contactsNotExportedCount, setContactsNotExportedCount] = useState(0);
    const [listName, setListName] = useState('');

    useEffect(() => {
        Context.mailChimpManager.getInitialData(
            filters.SearchFilters,
            filters.operators,
            (data, lists) => {
                setLoading(false);
                setCompanies(data.companiesCount);
                setContacts(data.contactsCount);
                setMailChimpLists(lists);
            },
            () => {
                setLoading(false);
            },
        );
        // Only fetch initial data once when the dialog mounts.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const total = companies + contacts;
    const totalSelected = (selectAccounts ? companies : 0) + (selectContacts ? contacts : 0);
    const hasNotExported = companiesNotExportedCount > 0 || contactsNotExportedCount > 0;
    const exportedCount = companiesExportedCount + contactsExportedCount;
    const notExportedCount = companiesNotExportedCount + contactsNotExportedCount;

    const isConfirmDisabled = useMemo(() => {
        const isInFirstStep = !currentSection && !(selectAccounts || selectContacts);
        const isInSecondStepSelect = currentSection && selectedList === null && newList === '';
        const isInSecondStepInput = currentSection && newList !== '' && newListExist;
        return isInFirstStep || isInSecondStepSelect || isInSecondStepInput;
    }, [currentSection, newList, newListExist, selectAccounts, selectContacts, selectedList]);

    const exportList = useCallback(
        (searchFilters, operators, entities, idList) => {
            setLoading(true);
            setIsExporting(true);

            Context.mailChimpManager.exportToMailchimp(
                searchFilters,
                operators,
                entities,
                idList,
                (response) => {
                    const resolvedListName = selectedList?.id ? selectedList.text : newList;

                    setLoading(false);
                    setIsExporting(false);
                    setExportFinished(true);
                    setCompaniesExportedCount(response.companiesExportedCount);
                    setContactsExportedCount(response.contactsExportedCount);
                    setCompaniesNotExportedCount(
                        response.companiesNotExportedCount > 0
                            ? response.companiesNotExportedCount
                            : 0,
                    );
                    setContactsNotExportedCount(
                        response.contactsNotExportedCount > 0
                            ? response.contactsNotExportedCount
                            : 0,
                    );
                    setListName(resolvedListName);
                },
                () => {
                    setLoading(false);
                    setIsExporting(false);
                    setExportFinished(true);
                    errorToast({ text: getLiteral('error_error') });
                },
            );
        },
        [newList, selectedList],
    );

    const exportToMailchimp = useCallback(() => {
        let entities = '';
        if (selectAccounts) entities += 'company;';
        if (selectContacts) entities += 'contact;';

        if (isList) {
            exportList(filters.SearchFilters, filters.operators, entities, selectedList.id);
            return;
        }

        if (newList) {
            setLoading(true);
            Context.mailChimpManager.createMailChimpList(
                newList,
                (response) => {
                    exportList(filters.SearchFilters, filters.operators, entities, response);
                },
                () => {
                    setLoading(false);
                    errorToast({ text: getLiteral('error_error') });
                },
            );
        }
    }, [
        exportList,
        filters.operators,
        filters.SearchFilters,
        isList,
        newList,
        selectAccounts,
        selectContacts,
        selectedList,
    ]);

    const switchField = useCallback(() => {
        setIsList((prev) => !prev);
        setSelectedList(null);
        setNewListExist(false);
        setNewList('');
    }, []);

    const onChangeNewList = useCallback(
        (value) => {
            const exist = mailChimpLists.filter((element) => element.name === value).length > 0;
            setNewList(value);
            setNewListExist(exist);
        },
        [mailChimpLists],
    );

    const changeSection = useCallback(() => {
        setCurrentSection((prev) => !prev);
    }, []);

    const closeMailchimp = useCallback(() => {
        onCloseCallback?.();
        setOpen(false);
        setLoading(false);
        setIsExporting(false);
        setExportFinished(false);
    }, [onCloseCallback]);

    const handleCancel = useCallback(() => {
        if (currentSection) {
            changeSection();
            return;
        }
        onCloseCallback?.();
        setOpen(false);
    }, [changeSection, currentSection, onCloseCallback]);

    const handleConfirm = useCallback(() => {
        if (exportFinished) {
            closeMailchimp();
            return;
        }
        if (!currentSection) {
            changeSection();
            return;
        }
        exportToMailchimp();
    }, [changeSection, closeMailchimp, currentSection, exportFinished, exportToMailchimp]);

    const toggleSection = (
        <div className="fm-mailchimp-dialog__section">
            <div className="fm-mailchimp-dialog__row fm-mailchimp-dialog__row--header">
                <Text>{getLiteral('label_mailchimp_number_accounts')}</Text>
                <Text className="fm-mailchimp-dialog__row-value">{total}</Text>
            </div>

            <div className="fm-mailchimp-dialog__toggles">
                <div className="fm-mailchimp-dialog__toggle-row">
                    <BoolField
                        label={getLiteral('label_mailchimp_number_accounts_mail')}
                        onChange={setSelectAccounts}
                        value={selectAccounts}
                    />
                    <Text className="fm-mailchimp-dialog__row-value">{companies}</Text>
                </div>
                <div className="fm-mailchimp-dialog__toggle-row">
                    <BoolField
                        label={getLiteral('label_mailchimp_number_contacts_mail')}
                        onChange={setSelectContacts}
                        value={selectContacts}
                    />
                    <Text className="fm-mailchimp-dialog__row-value">{contacts}</Text>
                </div>
            </div>

            <div className="fm-mailchimp-dialog__row fm-mailchimp-dialog__row--footer">
                <Text className="fm-mailchimp-dialog__row-label--bold">
                    {getLiteral('label_mailchimp_export')}
                </Text>
                <Text className="fm-mailchimp-dialog__row-value--bold">{totalSelected}</Text>
            </div>
        </div>
    );

    const mailchimpListSection = (
        <div className="fm-mailchimp-dialog__section">
            <Text className="fm-mailchimp-dialog__assign-title">
                {getLiteral('label_mailchimp_name_new_list')}
            </Text>
            <Text className="fm-mailchimp-dialog__assign-body">
                {getLiteral('helptext_mailchimp_new_list')}
            </Text>
            <div className="fm-mailchimp-dialog__assign-field">
                {isList ? (
                    <ValueList
                        canCreate={canCreate}
                        mailChimpLists={mailChimpLists}
                        onSelect={setSelectedList}
                        switchField={switchField}
                        value={selectedList}
                    />
                ) : (
                    <TextField
                        newListExist={newListExist}
                        onChange={onChangeNewList}
                        switchField={switchField}
                        value={newList}
                    />
                )}
            </div>
        </div>
    );

    const renderSuccessFinish = () => {
        const mailchimpImg = `${PATH_IMAGE}Mailchimp.svg`;
        const summaryHtml = getLiteralWithParameters('label_mailchimp_success_summary', [
            exportedCount,
            listName,
        ]);

        return (
            <div className="fm-mailchimp-dialog__confirm">
                <div className="fm-mailchimp-dialog__confirm-image">
                    <img alt="Mailchimp" src={mailchimpImg} />
                </div>
                <div className="fm-mailchimp-dialog__confirm-text">
                    <div className="fm-mailchimp-dialog__confirm-message">
                        <Icon
                            className="fm-mailchimp-dialog__status-icon--success"
                            name="thickEnabledFilled"
                            size="large"
                        />
                        <div
                            className="fm-mailchimp-dialog__confirm-summary"
                            dangerouslySetInnerHTML={{ __html: summaryHtml }}
                        />
                    </div>
                </div>
            </div>
        );
    };

    const renderFailFinish = () => (
        <div className="fm-mailchimp-dialog__finish-error">
            <AbsoluteError subtitle={getLiteral('label_mailchimp_fail_summary')} />
        </div>
    );

    const renderContent = () => {
        if (loading) {
            return (
                <div className="fm-mailchimp-dialog__loader">
                    <Loader size="medium" />
                    {isExporting && (
                        <Text className="fm-mailchimp-dialog__loader-text">
                            {getLiteral('help_mailchimp_export_wait')}
                        </Text>
                    )}
                </div>
            );
        }

        if (exportFinished) {
            if (hasNotExported) {
                return <MailChimpNotExported notExportedCount={notExportedCount} />;
            }

            return exportedCount > 0 ? renderSuccessFinish() : renderFailFinish();
        }

        return currentSection ? mailchimpListSection : toggleSection;
    };

    const isNotExportedView = exportFinished && hasNotExported;
    const isFinishView = exportFinished && !hasNotExported;
    const isSuccessFinish = isFinishView && exportedCount > 0;
    const isFailFinish = isFinishView && exportedCount === 0;
    const showModalFooter = !loading && (!exportFinished || isNotExportedView || isFinishView);

    const cancelText =
        isNotExportedView || isFinishView
            ? undefined
            : getLiteral(!currentSection ? 'action_cancel' : 'action_back');

    const confirmText = isNotExportedView
        ? getLiteral('action_close')
        : isFailFinish
          ? getLiteral('action_cancel')
          : isSuccessFinish
            ? getLiteral('action_mailchimp_final')
            : getLiteral(!currentSection ? 'action_next' : 'title_excel_export_email');

    return (
        <Modal
            cancelText={cancelText}
            className="fm-mailchimp-dialog"
            confirmText={confirmText}
            isConfirmDisabled={isNotExportedView || isFinishView ? false : isConfirmDisabled}
            isOpen={open}
            onCancel={
                showModalFooter && !isNotExportedView && !isFinishView ? handleCancel : undefined
            }
            onConfirm={showModalFooter ? handleConfirm : undefined}
            onRequestClose={exportFinished ? closeMailchimp : handleCancel}
            title={getLiteral('title_mailchimp_export')}
        >
            <div
                className={classnames('fm-mailchimp-dialog__body', {
                    'fm-mailchimp-dialog__body--error': isNotExportedView,
                    'fm-mailchimp-dialog__body--finish': isSuccessFinish,
                    'fm-mailchimp-dialog__body--finish-fail': isFailFinish,
                })}
            >
                {renderContent()}
            </div>
        </Modal>
    );
};

export default memo(MailChimpDialog);
